/**
 * `GET/POST/DELETE /tenants/{t}/invitations` (§18.2).
 *
 * The issue mutation's 201 body (`issuedInvitationSchema`) is a distinct
 * shape from the list row: it is the only place the invitation's link is
 * ever returned, and both mutations invalidate the list so a screen that
 * just issued or revoked one sees it without a manual refetch.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { issuedInvitationSchema, type Invitation, type IssuedInvitation } from '../api/schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const INVITATIONS_QUERY_KEY = (tenantId: string) =>
  ['tenant', tenantId, 'invitations'] as const;

export function useInvitations(tenantId: string): UseQueryResult<Invitation[], ApiFailureError> {
  const client = useApiClient();

  return useQuery<Invitation[], ApiFailureError>({
    queryKey: INVITATIONS_QUERY_KEY(tenantId),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tenantInvitations, { tenantId }),
        GET_ROUTES.tenantInvitations.schema,
      );
      return items;
    },
  });
}

export function useIssueInvitation(
  tenantId: string,
): UseMutationResult<
  IssuedInvitation,
  ApiFailureError,
  { email: string; role: string; campaign_id?: string | null }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      client.postParsed(`/tenants/${tenantId}/invitations`, body, issuedInvitationSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY(tenantId) });
    },
  });
}

export function useRevokeInvitation(
  tenantId: string,
): UseMutationResult<void, ApiFailureError, { invitationId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invitationId }) =>
      client.del(`/tenants/${tenantId}/invitations/${invitationId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY(tenantId) });
    },
  });
}
